/* DemoFillPlanButton — Planner-side demo-fill control.
 *
 * Mirrors the doctor-side DemoFillButton's UX so the planner can
 * one-click populate every accordion in the Author-the-plan tab
 * (bite metrics, per-tooth plan, proposed protocol, aligners & wear,
 * retention, deliverable). Picks one of three pre-seeded plan
 * templates from the backend (mild / moderate / complex) — defaults
 * to mild so a single click "just works", and clicking again rotates
 * through the templates for variety during demos.
 *
 * Self-renders nothing in production builds (NODE_ENV === "production")
 * because demo fills shouldn't ship to clinics. Visible only in
 * preview / dev environments.
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MagicWand, ArrowsClockwise } from "@phosphor-icons/react";


const TEMPLATES = [
    { idx: 0, label: "Mild — Class I, light crowding" },
    { idx: 1, label: "Moderate — Class II Div 1" },
    { idx: 2, label: "Complex — Class III, openbite, extractions" },
];


export default function DemoFillPlanButton({ caseId, onFilled }) {
    const [busy, setBusy] = useState(false);
    const [scenarioIdx, setScenarioIdx] = useState(0);

    if (!caseId) return null;
    /* Hide in true production builds — demos shouldn't leak into a live
       clinic environment. Preview/dev still expose the button. */
    if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_INVISIQUE_PREVIEW) {
        return null;
    }

    const onClick = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const resp = await api.post(
                `/demo/plans/${caseId}/fill?scenario_idx=${scenarioIdx}`,
            );
            toast.success(`Plan filled — ${resp?.data?.label || "demo template"}`);
            setScenarioIdx((i) => (i + 1) % TEMPLATES.length);
            if (typeof onFilled === "function") onFilled(resp?.data?.plan);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed to fill demo plan";
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    const tpl = TEMPLATES[scenarioIdx];
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold border border-dashed border-primary/40 text-primary/80 hover:bg-primary/5 hover:border-primary/60 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="planner-plan-demo-fill"
            title={`Demo fill: ${tpl.label} (clicks rotate through ${TEMPLATES.length} templates)`}
        >
            {busy ? (
                <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
            ) : (
                <MagicWand size={12} weight="duotone" />
            )}
            <span className="hidden sm:inline">Demo fill plan</span>
            <span className="text-[10px] text-muted-foreground/70 font-mono tabular-nums">
                #{scenarioIdx + 1}
            </span>
        </button>
    );
}
